import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ArrowUpRight, Menu, ShieldCheck, X } from 'lucide-react'
import { Brand } from './Brand'

const navigation = [
  { label: 'About', to: '/about' },
  { label: 'Insights', to: '/insights' },
  { label: 'Apps', to: '/apps' },
  { label: 'Research', to: '/research' },
  { label: 'Community', to: '/community' },
]

export function Layout() {
  const [open, setOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const menuWasOpen = useRef(false)
  const location = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    const frame = window.requestAnimationFrame(() => setOpen(false))
    return () => window.cancelAnimationFrame(frame)
  }, [location.pathname])

  useEffect(() => {
    document.body.classList.toggle('menu-open', open)
    return () => document.body.classList.remove('menu-open')
  }, [open])

  useEffect(() => {
    if (open) {
      menuWasOpen.current = true
      closeButtonRef.current?.focus()

      const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') setOpen(false)
      }
      const closeOnDesktop = () => {
        if (window.innerWidth > 860) setOpen(false)
      }

      window.addEventListener('keydown', closeOnEscape)
      window.addEventListener('resize', closeOnDesktop)
      return () => {
        window.removeEventListener('keydown', closeOnEscape)
        window.removeEventListener('resize', closeOnDesktop)
      }
    }

    if (menuWasOpen.current) {
      menuWasOpen.current = false
      menuButtonRef.current?.focus()
    }
  }, [open])

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="site-header">
        <div className="nav-wrap container">
          <Brand />
          <nav className="main-nav desktop-navigation" aria-label="Main navigation">
            <div className="nav-links">
              {navigation.map((item) => (
                <NavLink key={item.to} to={item.to}>{item.label}</NavLink>
              ))}
            </div>
            <NavLink className="button button-small nav-cta" to="/booking">Book a visit <ArrowUpRight size={16} /></NavLink>
          </nav>
          <button
            ref={menuButtonRef}
            className="icon-button menu-button"
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-controls="mobile-navigation"
            aria-expanded={open}
          >
            <Menu />
          </button>
        </div>
      </header>

      {open && createPortal(
        <aside id="mobile-navigation" className="mobile-nav-drawer" role="dialog" aria-modal="true" aria-label="Mobile navigation">
          <div className="mobile-nav-top">
            <Brand />
            <button ref={closeButtonRef} className="icon-button mobile-nav-close" type="button" onClick={() => setOpen(false)} aria-label="Close menu"><X /></button>
          </div>
          <nav className="mobile-nav-links" aria-label="Mobile navigation links">
            {navigation.map((item, index) => (
              <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                {item.label}
                <ArrowUpRight size={18} />
              </NavLink>
            ))}
          </nav>
          <NavLink className="button mobile-nav-cta" to="/booking" onClick={() => setOpen(false)}>Book a visit <ArrowUpRight size={17} /></NavLink>
          <p className="mobile-nav-note"><ShieldCheck size={17} /> Educational content. Your care stays personal.</p>
        </aside>,
        document.body,
      )}

      <main id="main-content"><Outlet /></main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <Brand />
            <p>Thoughtful medicine, useful technology, and clearer health conversations.</p>
            <a className="social-link" href="https://www.linkedin.com/in/fathima-finu-3330123b8" target="_blank" rel="noreferrer">
              <span className="linkedin-glyph" aria-hidden="true">in</span> LinkedIn <ArrowUpRight size={15} />
            </a>
          </div>
          <div>
            <h3>Explore</h3>
            <NavLink to="/insights">Health insights</NavLink>
            <NavLink to="/apps">Patient apps</NavLink>
            <NavLink to="/research">Research watch</NavLink>
            <NavLink to="/community">Share an idea</NavLink>
          </div>
          <div>
            <h3>Care</h3>
            <NavLink to="/booking">Book a visit</NavLink>
            <NavLink to="/about">About the doctor</NavLink>
            <NavLink to="/privacy">Privacy & safety</NavLink>
            <NavLink to="/editorial-policy">Editorial policy</NavLink>
            <span className="muted-link">Clinic details coming soon</span>
          </div>
          <div className="footer-note">
            <span className="eyebrow">A gentle reminder</span>
            <p>This website shares general education, not a diagnosis or a substitute for individual medical care.</p>
            <strong>For an emergency, contact your local emergency service now.</strong>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} Dr. Fathima Finu M. P.</span>
          <span>Last source check: 3 October 2026</span>
        </div>
      </footer>
    </div>
  )
}
